/** Vendor names match after trim and case folding so "Home Depot" and "home depot" share a PO. */
export function normalizeVendorName(raw: string | null | undefined): string {
  return String(raw || '').replace(/\s+/g, ' ').trim().slice(0, 200);
}

export function vendorMatchKey(raw: string | null | undefined): string {
  return normalizeVendorName(raw).toLowerCase();
}

export type PurchaseVendorLine = {
  vendorName?: string | null;
  poNumber?: string | null;
};

const UNNAMED_VENDOR_KEY = '__none__';

/** A line with no vendor uses the requisition header vendor. */
export function effectiveLineVendor(
  line: PurchaseVendorLine,
  headerVendor?: string | null,
): string {
  return normalizeVendorName(line.vendorName) || normalizeVendorName(headerVendor);
}

/**
 * Shared store when every line that names a vendor names the same one.
 * When no line names a vendor, the header vendor still applies to the request.
 */
export function headerVendorFromLines(
  lines: PurchaseVendorLine[],
  explicitHeader?: string | null,
): string {
  const named = lines.map(line => normalizeVendorName(line.vendorName)).filter(Boolean);
  if (!named.length) return normalizeVendorName(explicitHeader);
  const key = vendorMatchKey(named[0]);
  if (named.every(name => vendorMatchKey(name) === key)) return named[0];
  return '';
}

export type VendorGroup<T> = {
  vendorName: string;
  key: string;
  lines: T[];
};

export function groupLinesByVendor<T extends PurchaseVendorLine>(
  lines: T[],
  headerVendor?: string | null,
): VendorGroup<T>[] {
  const groups: VendorGroup<T>[] = [];
  const index = new Map<string, VendorGroup<T>>();
  for (const line of lines) {
    const vendorName = effectiveLineVendor(line, headerVendor);
    const key = vendorMatchKey(vendorName) || UNNAMED_VENDOR_KEY;
    let group = index.get(key);
    if (!group) {
      group = { vendorName, key, lines: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.lines.push(line);
  }
  return groups;
}

/**
 * Drop issued per-line PO stamps (CRM #73).
 * Return / resubmit / vendor edits must not leave old numbers that collide with
 * other purchase orders after the header `poNumber` was cleared.
 */
export function clearLinePoNumbers<T extends PurchaseVendorLine>(
  lines: T[],
): Array<Omit<T, 'poNumber'> & { poNumber?: undefined }> {
  return lines.map(line => {
    const { poNumber: _drop, ...rest } = line;
    return rest;
  });
}

/** Stamp the PO for each vendor group, and the effective vendor, onto every line. */
export function assignPoNumbersToLines<T extends PurchaseVendorLine>(
  lines: T[],
  headerVendor: string | null | undefined,
  poNumbers: string[],
): Array<Omit<T, 'poNumber' | 'vendorName'> & { vendorName?: string; poNumber?: string }> {
  const groups = groupLinesByVendor(lines, headerVendor);
  const poByKey = new Map(groups.map((group, index) => [group.key, String(poNumbers[index] || '').trim()]));
  return lines.map(line => {
    const { poNumber: _prior, vendorName: _priorVendor, ...rest } = line;
    const vendorName = effectiveLineVendor(line, headerVendor);
    const key = vendorMatchKey(vendorName) || UNNAMED_VENDOR_KEY;
    const poNumber = poByKey.get(key) || '';
    return {
      ...rest,
      ...(vendorName ? { vendorName } : {}),
      // Never keep a prior stamp when this assign pass has no number for the group.
      ...(poNumber ? { poNumber } : {}),
    };
  });
}

/** True when the set of vendor groups (by match key) differs — store change needs new POs. */
export function vendorGroupKeysChanged(
  before: PurchaseVendorLine[],
  beforeHeader: string | null | undefined,
  after: PurchaseVendorLine[],
  afterHeader: string | null | undefined,
): boolean {
  const a = groupLinesByVendor(before, beforeHeader).map(g => g.key).sort().join('\0');
  const b = groupLinesByVendor(after, afterHeader).map(g => g.key).sort().join('\0');
  return a !== b;
}

/**
 * CRM #75: issued POs stamp each line's store. Editing only the header Vendor field
 * would otherwise leave line stamps (and look like a no-op). When there is still a
 * single store group, rewrite every line to the new header and drop PO stamps.
 * Multi-store requisitions keep per-line vendors untouched.
 */
export function rewriteUniformVendorLinesOnHeaderChange<T extends PurchaseVendorLine>(
  lines: T[],
  previousHeader: string | null | undefined,
  nextHeader: string,
): T[] {
  const groups = groupLinesByVendor(lines, previousHeader);
  if (groups.length !== 1) return lines;
  const next = normalizeVendorName(nextHeader);
  return lines.map(line => {
    const { poNumber: _priorPo, ...rest } = line;
    return {
      ...(rest as T),
      vendorName: next,
    };
  });
}

export function purchaseOrderNumbers(row: {
  poNumber?: string | null;
  lineItems?: Array<{ poNumber?: string | null }>;
}): string[] {
  const numbers: string[] = [];
  const push = (value?: string | null) => {
    const number = String(value || '').trim();
    if (number && !numbers.includes(number)) numbers.push(number);
  };
  for (const line of row.lineItems || []) push(line.poNumber);
  if (!numbers.length) push(row.poNumber);
  return numbers;
}

export function issuedVendorGroups<T extends PurchaseVendorLine>(
  lines: T[],
  headerVendor?: string | null,
  headerPo?: string | null,
): Array<VendorGroup<T> & { poNumber: string }> {
  const groups = groupLinesByVendor(lines, headerVendor);
  const header = String(headerPo || '').trim();
  return groups.map(group => {
    const fromLine = group.lines.map(line => String(line.poNumber || '').trim()).find(Boolean) || '';
    return {
      ...group,
      poNumber: fromLine || (groups.length === 1 ? header : ''),
    };
  });
}

/** Issued purchase orders when one requisition became more than one PO. Otherwise empty. */
export function forkedPurchaseOrders<T extends PurchaseVendorLine>(
  lines: T[],
  headerVendor?: string | null,
  headerPo?: string | null,
): Array<VendorGroup<T> & { poNumber: string }> {
  const groups = issuedVendorGroups(lines, headerVendor, headerPo).filter(group => group.poNumber);
  return groups.length > 1 ? groups : [];
}

/** One vendor, or "Home Depot PO-1, American Welding PO-2" once several POs exist. */
export function purchaseVendorPoLabel(
  lines: PurchaseVendorLine[],
  headerVendor?: string | null,
  headerPo?: string | null,
): string {
  const groups = issuedVendorGroups(lines, headerVendor, headerPo).filter(
    group => group.vendorName || group.poNumber,
  );
  if (!groups.length) {
    return [normalizeVendorName(headerVendor), String(headerPo || '').trim()].filter(Boolean).join(' · ');
  }
  if (groups.length === 1) {
    return [groups[0].vendorName, groups[0].poNumber].filter(Boolean).join(' · ');
  }
  return groups
    .map(group => [group.vendorName || 'Vendor not set', group.poNumber].filter(Boolean).join(' '))
    .join(', ');
}
